import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Download, Printer, Loader2, FileText, X, Share2, Sparkles } from "lucide-react";
import PdfPagesView from "@/components/PdfPagesView";
import { printPdf } from "@/lib/quotePdf";
import { buildOrderFormPdf, orderFormFilename } from "@/lib/orderFormPdf";
import { COMPANY } from "@/lib/company";
import { DEFAULT_PDF_OPTS, DEFAULT_TITLE } from "@/components/orders/orderFormUtils";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/** Sipariş talep formu önizleme — ekranda görünen = indirilen = yazdırılan PDF (vektörel). */
export default function OrderFormPreviewDialog({ form, onClose, onOptsChange }) {
  const [opts, setOpts] = useState({ ...DEFAULT_PDF_OPTS, ...(form?.pdf_opts || {}) });
  const [busy, setBusy] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const pdfRef = useRef(null);
  const width = Math.min(900, (typeof window !== "undefined" ? window.innerWidth : 900) - 80);
  useEffect(() => { if (form) setOpts({ ...DEFAULT_PDF_OPTS, ...(form.pdf_opts || {}) }); }, [form?.id, form?.form_no]); // eslint-disable-line
  const set = (k) => (v) => { const n = { ...opts, [k]: v }; setOpts(n); onOptsChange && onOptsChange(n); };

  useEffect(() => {
    if (!form) { setPdfData(null); pdfRef.current = null; return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const pdf = await buildOrderFormPdf(form, opts);
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.getNumberOfPages());
        setPdfData(pdf.output("arraybuffer"));
      } catch (e) { console.error(e); if (!cancelled) toast.error("Önizleme oluşturulamadı"); }
      finally { if (!cancelled) setBusy(false); }
    }, 100);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form, opts]);

  const ready = () => { if (!pdfRef.current) { toast.error("PDF henüz hazır değil"); return null; } return pdfRef.current; };
  const download = () => { const pdf = ready(); if (!pdf) return; pdf.save(orderFormFilename(form)); toast.success("Sipariş formu PDF indirildi"); };
  const print = () => { const pdf = ready(); if (!pdf) return; printPdf(pdf, orderFormFilename(form).replace(/\.pdf$/, ""), { asFileName: true }); };
  const share = async () => {
    const pdf = ready(); if (!pdf) return;
    try {
      const blob = pdf.output("blob");
      const file = new File([blob], orderFormFilename(form), { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: form.title || "Sipariş Talep Formu", text: `${COMPANY.name} — ${form.title || "Sipariş Talep Formu"} (${form.form_no || "taslak"})` });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const phone = String(form.supplier_phone || "").replace(/\D/g, "");
      const msg = encodeURIComponent(`Merhaba, ${COMPANY.name} olarak ${form.title || "sipariş talep formumuzu"} ekte iletiyoruz (${(form.items || []).length} kalem). Fiyat ve termin bilgisi rica ederiz.`);
      window.open(phone ? `https://wa.me/${phone.startsWith("0") ? "9" + phone : phone}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
      toast.success("PDF indirildi — WhatsApp'ta dosyayı ekleyip gönderin");
    } catch (e) { console.error(e); toast.error("Paylaşım başarısız"); }
  };

  const Toggle = ({ k, label, testid }) => (
    <label className={cn("inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none rounded-full border px-2.5 py-1 transition-colors", opts[k] ? "bg-primary/10 border-primary/30 text-primary" : "bg-card text-muted-foreground hover:text-foreground")}>
      <Switch checked={!!opts[k]} onCheckedChange={set(k)} data-testid={testid} className="scale-75 -ml-1" />{label}
    </label>
  );

  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-[96vw] sm:max-w-5xl max-h-[95vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="of-preview">
        <div className="shrink-0 relative bg-[#0B192C] text-white px-4 sm:px-5 py-3 overflow-hidden">
          <span className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-primary/30 blur-3xl" />
          <span className="pointer-events-none absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-primary via-orange-300 to-primary" />
          <div className="relative flex flex-wrap items-center gap-2">
            <span className="h-9 w-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center"><FileText className="h-4 w-4 text-[#ffb27a]" /></span>
            <div className="mr-auto min-w-0 flex-1 basis-[260px]">
              <DialogTitle className="font-head text-lg font-black tracking-tight leading-tight flex items-center gap-2 flex-wrap">
                Sipariş Talep Formu
                <span className="font-mono text-sm font-bold text-[#ffb27a] bg-white/10 rounded px-1.5 py-0.5" data-testid="of-preview-no">{form?.form_no || "TASLAK"}</span>
                {pageCount > 0 && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300 bg-white/10 rounded-full px-2 py-0.5" data-testid="of-preview-pages">{pageCount} sayfa</span>}
              </DialogTitle>
              <p className="text-[11px] text-slate-300 truncate hidden sm:block">{form?.title || DEFAULT_TITLE}{form?.supplier_title ? ` · ${form.supplier_title}` : ""} · Gördüğünüz belge, indirilen / yazdırılan PDF ile birebir aynıdır.</p>
            </div>
            <Button size="sm" onClick={download} disabled={busy || !pdfData} data-testid="of-preview-download" className="shadow-md">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}PDF İndir</Button>
            <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white" onClick={share} disabled={busy || !pdfData} data-testid="of-preview-share"><Share2 className="h-4 w-4 mr-1.5" />Paylaş</Button>
            <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white" onClick={print} disabled={busy || !pdfData} data-testid="of-preview-print"><Printer className="h-4 w-4 mr-1.5" />Yazdır</Button>
            <button type="button" onClick={() => onClose && onClose()} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="of-preview-close" title="Kapat"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="shrink-0 border-b bg-card px-4 sm:px-5 py-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1 font-head"><Sparkles className="h-3 w-3 text-primary" />Görünüm</span>
          <Toggle k="showBrand" label="Marka" testid="of-pv-brand" />
          <Toggle k="showCodes" label="Ürün kodu" testid="of-pv-codes" />
          <Toggle k="showNotes" label="Açıklama" testid="of-pv-notes" />
          <Toggle k="showBlankCols" label="Boş teklif fiyatı sütunu" testid="of-pv-blank" />
          <Toggle k="showTermin" label="Termin sütunu" testid="of-pv-termin" />
          <Toggle k="showPrices" label="Fiyat & maliyet" testid="of-pv-prices" />
          {opts.showPrices && <span className="text-[10.5px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="of-pv-prices-warn">Maliyetleriniz PDF'te görünür!</span>}
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6 bg-[radial-gradient(circle_at_top,_#e2e8f0_0%,_#cbd5e1_100%)]" data-testid="of-preview-body">
          {pdfData ? <PdfPagesView data={pdfData} width={width} testid="of-preview-pdf" />
            : <div className="flex items-center justify-center py-32 text-sm text-muted-foreground" data-testid="of-preview-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" />Form hazırlanıyor…</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
